import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import cacheManager from '../CacheManager';
import { formatDate } from '../js/dateFormatter';
import { showSuccess, showError } from '../utils/toast';

const TELEFONE_MAX_LENGTH = 20;

const PgClienteEditar = ({ userId }) => {
    const { id } = useParams(); // Extrai o ID do cliente da URL
    const [cliente, setCliente] = useState({
        nome: '',
        tipo_cadastro: 'PF',
        estado_civil: '',
        profissao: '',
        telefone: '',
        telefone_flag_whatsapp: 0,
        nascimento: '',
        cpf: '',
        rg: '',
        rg_orgao_emissor: '',
        email: '',
        como_conheceu: '',
        contrato_data_assinatura: '',
        cli_endereco: '',
        cli_bairro: '',
        cli_cidade: '',
        cli_uf: '',
        cli_cep: '',
    });
    const [isLoading, setIsLoading] = useState(false); // Estado para controlar o carregamento
    const [cpfError, setCpfError] = useState(false); // Estado para validação do CPF
    const [telefoneError, setTelefoneError] = useState(false); // Estado para validação do telefone
    
    const navigate = useNavigate();
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    useEffect(() => {
        if (id) {
            setIsButtonDisabled(false)
            setIsLoading(true); // Inicia o carregamento
            fetch(`https://api.faeworks.com.br/cliente/read.php?id=${id}`)
                .then(response => response.json())
                .then(data => {
                    if (data && data.length > 0) {
                        const cliente = {
                            ...data[0],
                            tipo_cadastro: data[0].tipo_cadastro || 'PF',
                            telefone_flag_whatsapp: Number(data[0].telefone_flag_whatsapp || 0),
                        };
                        setCliente(cliente);
                    }
                })
                .catch(error => console.error('Erro ao buscar dados do cliente:', error))
                .finally(() => setIsLoading(false)); // Finaliza o carregamento
        }
    }, [id]); // Dependência: id

    const atualizarCliente = async () => {
        try {
            setIsLoading(true);
            const url = id ? 'https://api.faeworks.com.br/cliente/update.php' : 'https://api.faeworks.com.br/cliente/create.php';
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: JSON.stringify(cliente)
            });

            const result = await response.json();

            if (result.status === 'success') {
                setIsLoading(false);
                showSuccess('Cliente atualizado com sucesso!');
                cacheManager.invalidateClientesCache();
                handleVoltarClick();
            } else {
                setIsLoading(false);
                showError('Erro ao atualizar cliente: ' + result.message);
                console.error('Erro ao atualizar cliente:', result.message);
            }
        } catch (error) {
            setIsLoading(false);
            showError('Erro na requisição: ' + error.message);
            console.error('Erro na requisição:', error);
        }
    };

    // Estilos para esmaecer a página
    const overlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 2, // Garanta que esteja acima de outros elementos da página
        display: isLoading ? 'block' : 'none', // Mostra apenas quando está carregando
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        const fieldValue = type === 'checkbox' ? (checked ? 1 : 0) : value;

                if (name === 'telefone' && value.length > TELEFONE_MAX_LENGTH) {
                        setTelefoneError(true);
                        return;
                }

        setCliente((prevCliente) => ({
          ...prevCliente,
          [name]: fieldValue
        }));
        
        // Limpar erro do CPF quando o usuário digitar
        if (name === 'cpf') {
            setCpfError(false);
        }
        
        // Limpar erro do telefone quando o usuário digitar
        if (name === 'telefone') {
            setTelefoneError(false);
        }
    };

    const formatarCPF_CNPJ = (valor) => {
        // Remove tudo que não é número
        const numeros = valor.replace(/\D/g, '');
        
        if (numeros.length === 11) {
            // Formata CPF: 000.000.000-00
            return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        } else if (numeros.length === 14) {
            // Formata CNPJ: 00.000.000/0000-00
            return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        
        // Se não tiver 11 ou 14 dígitos, retorna null (inválido)
        return null;
    };

    const handleBlurCPF = (e) => {
        const valor = e.target.value.trim();
        
        // Se estiver vazio, não aplica formatação nem erro
        if (valor === '') {
            setCpfError(false);
            return;
        }
        
        const valorFormatado = formatarCPF_CNPJ(valor);
        
        if (valorFormatado) {
            setCliente((prevCliente) => ({
                ...prevCliente,
                cpf: valorFormatado
            }));
            setCpfError(false);
        } else {
            // Se tem valor mas não é válido, marca como erro mas mantém o valor
            setCpfError(true);
        }
    };

    const formatarTelefone = (valor) => {
        // Remove tudo que não é número
        const numeros = valor.replace(/\D/g, '');
        
        if (numeros.length === 8) {
            // Formato: 1234-5678
            return numeros.replace(/(\d{4})(\d{4})/, '$1-$2');
        } else if (numeros.length === 9) {
            // Formato: 91234-5678
            return numeros.replace(/(\d{5})(\d{4})/, '$1-$2');
        } else if (numeros.length === 10) {
            // Formato: (21) 1234-5678
            return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        } else if (numeros.length === 11) {
            // Formato: (21) 91234-5678
            return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
        
        // Para números internacionais (ou outros formatos), mantém o valor informado
        return valor;
    };

    const handleBlurTelefone = (e) => {
        const valor = e.target.value.trim();
        const numeros = valor.replace(/\D/g, '');
        
        // Se estiver vazio, não aplica formatação nem erro
        if (valor === '') {
            setTelefoneError(false);
            return;
        }

        if (valor.length > TELEFONE_MAX_LENGTH) {
            setTelefoneError(true);
            return;
        }

        // Mantém erro apenas quando não há nenhum dígito informado
        if (numeros.length === 0) {
            setTelefoneError(true);
            return;
        }
        
        const valorFormatado = formatarTelefone(valor);

        setCliente((prevCliente) => ({
            ...prevCliente,
            telefone: valorFormatado
        }));
        setTelefoneError(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault(); // Prevenir o comportamento padrão do formulário

        if ((cliente.telefone || '').length > TELEFONE_MAX_LENGTH) {
            setTelefoneError(true);
            showError('Telefone deve ter no máximo 20 caracteres');
            return;
        }

        await atualizarCliente(); // Assumindo que atualizarCliente agora manipula a lógica de atualização e redirecionamento
    };

    const handleVoltarClick = () => {
        navigate('/clientes');
    };

    const handleButtonContratoClick = async () => {
        // Desabilitar o botão
        setIsButtonDisabled(true);
        try {
          const response = await fetch(`https://api.faeworks.com.br/termo/enviar.php?id=${id}`);
          const data = await response.json();
          console.log(data);
          
          if (data.status === 'success') {
            showSuccess(data.message || 'Termo enviado com sucesso!');
          } else {
            showError(data.message || 'Erro ao enviar o termo.');
          }
        } catch (error) {
          console.error('Erro ao enviar o termo:', error);
          showError('Erro ao enviar o termo.');
        }
        setIsButtonDisabled(false);
    };

    const handleEnviarCadastroSenhaClick = async () => {
        // Desabilitar o botão
        setIsButtonDisabled(true);
        try {
          const response = await fetch(`https://api.faeworks.com.br/senha/enviar.php?id=${id}`);
          const data = await response.json();
          console.log(data);
          
          if (data.status === 'success') {
            showSuccess(data.message || 'Link de cadastro de senha enviado com sucesso!');
          } else {
            showError(data.message || 'Erro ao enviar o link de cadastro.');
          }
        } catch (error) {
          console.error('Erro ao enviar link de cadastro:', error);
          showError('Erro ao enviar o link de cadastro.');
        }
        setIsButtonDisabled(false);
    };

    const handleRedefinirSenhaClick = async () => {
        const novaSenha = prompt('Digite a nova senha para o cliente:');
        
        if (!novaSenha) {
            return; // Usuário cancelou
        }

        if (novaSenha.length < 4) {
            showError('A senha deve ter no mínimo 4 caracteres.');
            return;
        }

        try {
            setIsLoading(true);
            const response = await fetch('https://api.faeworks.com.br/cliente/update.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id_cliente: id,
                    senha: novaSenha
                })
            });

            const result = await response.json();

            if (result.status === 'success') {
                showSuccess('Senha redefinida com sucesso!');
            } else {
                showError('Erro ao redefinir senha: ' + result.message);
            }
        } catch (error) {
            console.error('Erro ao redefinir senha:', error);
            showError('Erro ao redefinir senha.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mt-5">
            {isLoading && <div style={overlayStyle}></div>} {/* Overlay de carregamento */}
            <h1 className="h1">{id ? 'Editar Cliente' : 'Novo Cliente'}</h1>
            <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                <div className="row">
                    <div className="col-12 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Tipo de cadastro:
                        </label>
                        <div className="form-check form-check-inline">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="tipo_cadastro"
                                id="tipoCadastroPF"
                                value="PF"
                                checked={(cliente.tipo_cadastro || 'PF') === 'PF'}
                                onChange={handleChange}
                            />
                            <label className="form-check-label" htmlFor="tipoCadastroPF">Pessoa Física (PF)</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="tipo_cadastro"
                                id="tipoCadastroPJ"
                                value="PJ"
                                checked={cliente.tipo_cadastro === 'PJ'}
                                onChange={handleChange}
                            />
                            <label className="form-check-label" htmlFor="tipoCadastroPJ">Pessoa Jurídica (PJ)</label>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-9 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Nome completo:
                            <input type="text" className="form-control" value={cliente.nome} name="nome" onChange={handleChange} />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            CPF:
                            <input 
                                type="text" 
                                className={`form-control ${cpfError ? 'is-invalid' : ''}`}
                                value={cliente.cpf} 
                                name="cpf" 
                                onChange={handleChange}
                                onBlur={handleBlurCPF}
                            />
                            {cpfError && (
                                <div className="invalid-feedback" style={{ display: 'block' }}>
                                    CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos
                                </div>
                            )}
                        </label>
                    </div>
                </div>

                <div className="row">
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Estado civil:
                            <input type="text" className="form-control" value={cliente.estado_civil || ''} name="estado_civil" onChange={handleChange} />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Profissão:
                            <input type="text" className="form-control" value={cliente.profissao || ''} name="profissao" onChange={handleChange} />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            RG:
                            <input type="text" className="form-control" value={cliente.rg || ''} name="rg" onChange={handleChange} />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Órgão emissor:
                            <input type="text" className="form-control" value={cliente.rg_orgao_emissor || ''} name="rg_orgao_emissor" onChange={handleChange} />
                        </label>
                    </div>
                </div>

                <div className="row">
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Telefone:
                            <input 
                                type="text" 
                                className={`form-control ${telefoneError ? 'is-invalid' : ''}`}
                                value={cliente.telefone} 
                                name="telefone" 
                                onChange={handleChange}
                                onBlur={handleBlurTelefone}
                                maxLength={TELEFONE_MAX_LENGTH}
                            />
                            {telefoneError && (
                                <div className="invalid-feedback" style={{ display: 'block' }}>
                                    Telefone deve ter no máximo 20 caracteres
                                </div>
                            )}
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Data de nascimento:
                            <input type="date" className="form-control" value={cliente.nascimento} name="nascimento" onChange={handleChange} />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            E-mail:
                            <input type="email" className="form-control" value={cliente.email} name="email" onChange={handleChange} />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Assinatura:
                            <input type="text" className="form-control" value={formatDate(cliente.contrato_data_assinatura)} readOnly />
                            
                        </label>
                    </div>
                </div>

                <div className="row">
                    <div className="col-3 mb-3 d-flex align-items-end">
                        <div className="form-check mt-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="telefoneWhatsapp"
                                name="telefone_flag_whatsapp"
                                checked={Number(cliente.telefone_flag_whatsapp || 0) === 1}
                                onChange={handleChange}
                            />
                            <label className="form-check-label" htmlFor="telefoneWhatsapp">
                                Número é WhatsApp?
                            </label>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 mb-12">
                        <label className="form-label" style={{ display: 'block' }}>
                            Rua/Avenida e número:
                            <input type="text" className="form-control" value={cliente.cli_endereco} name="cli_endereco" onChange={handleChange} />
                        </label>
                    </div>
                </div>

                <div className="row">
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            UF:
                            <input type="text" className="form-control" value={cliente.cli_uf || ''} name="cli_uf" onChange={handleChange} />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Cidade:
                            <input type="text" className="form-control" value={cliente.cli_cidade || ''} name="cli_cidade" onChange={handleChange} />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Bairro:
                            <input type="text" className="form-control" value={cliente.cli_bairro || ''} name="cli_bairro" onChange={handleChange} />
                        </label>
                    </div>
                    <div className="col-3 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            CEP:
                            <input type="text" className="form-control" value={cliente.cli_cep || ''} name="cli_cep" onChange={handleChange} />
                        </label>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 mb-3">
                        <label className="form-label" style={{ display: 'block' }}>
                            Como conheceu a FAEWorks?
                            <textarea
                                className="form-control"
                                rows="3"
                                value={cliente.como_conheceu || ''}
                                name="como_conheceu"
                                onChange={handleChange}
                            />
                        </label>
                    </div>
                </div>

                <div className="row">
                    <div className="col-6 d-flex gap-2">
                        {userId === 83 && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleButtonContratoClick}
                                    disabled={isButtonDisabled || !cliente.email || cliente.email.trim() === ''}
                                >
                                    Enviar termo
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-info"
                                    onClick={handleEnviarCadastroSenhaClick}
                                    disabled={isButtonDisabled || !cliente.email || cliente.email.trim() === ''}
                                >
                                    Enviar cadastro de senha
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            className="btn btn-warning"
                            onClick={handleRedefinirSenhaClick}
                            disabled={isLoading || !id}
                        >
                            Redefinir senha
                        </button>
                    </div>
                    <div className="col-6 d-flex justify-content-end">
                        <button type="button" className="btn btn-secondary me-2" onClick={handleVoltarClick}>Voltar</button>
                        <button type="submit" className="btn btn-primary">Salvar</button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PgClienteEditar;